// src/pages/SeasonRecap.js
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import API from "../api";
import Card from "../components/ui/Card";
import LoadingSpinner from "../components/LoadingSpinner";
import { usePageTitle } from "../hooks/usePageTitle";

import { SHOW_GROUPS } from "../constants/featureFlags";
import { CURRENT_SEASON, formatSeasonLabel } from "../constants/season";

export default function SeasonRecap() {
  const { seasonId } = useParams();
  const season = seasonId || CURRENT_SEASON;
  usePageTitle(`Season ${season} Recap`);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await API.get(`/season/${encodeURIComponent(season)}/recap`);
        if (!cancelled) {
          setData(data);
        }
      } catch (e) {
        console.error("Error loading season recap:", e);
        if (!cancelled) {
          setError(
            e?.response?.data?.message ||
              "Failed to load season recap. Please try again."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [season]);

  if (loading) {
    return <LoadingSpinner message="Loading season recap..." />;
  }

  if (error || !data) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-10">
        <Card>
          <p className="text-center text-red-600 py-6 text-sm md:text-base">
            {error || "Season recap not available."}
          </p>
        </Card>
      </div>
    );
  }

  const { champion, top_users = [], user_best_round, top_groups = [] } = data;

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      <main className="max-w-7xl mx-auto px-6 py-10 md:py-14 space-y-8">
        <Card>
          <h1 className="text-3xl md:text-4xl font-bold mb-2">
            Season {season} Recap
          </h1>
          <p className="text-sm md:text-base text-gray-700">
            A quick look back at the key highlights from this season: champion,
            your best round, and how the community performed.
          </p>
        </Card>

        {/* Champion & Your Best Round */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card title="Season Champion (Premier League)">
            {champion ? (
              <div>
                <p className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-1">
                  {champion.username}
                </p>
                <p className="text-sm text-gray-700">
                  {champion.total_points} pts across the season
                </p>
              </div>
            ) : (
              <p className="text-sm text-gray-600">No champion data yet.</p>
            )}
          </Card>

          <Card title="Your Best Round">
            {user_best_round ? (
              <div>
                <p className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-1">
                  {user_best_round.total_points} pts
                </p>
                <p className="text-sm text-gray-700">
                  {user_best_round.label}
                </p>
              </div>
            ) : (
              <p className="text-sm text-gray-600">
                Make predictions this season to unlock your best round.
              </p>
            )}
          </Card>
        </div>

        {/* Top 10 Users */}
        <Card title="Top 10 (Premier League)">
          {top_users.length === 0 ? (
            <p className="text-sm text-gray-600 py-4 text-center">
              No leaderboard data yet for this season.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-gray-500">
                    <th className="py-2 pr-4 text-left">Rank</th>
                    <th className="py-2 pr-4 text-left">User</th>
                    <th className="py-2 text-right">Points</th>
                  </tr>
                </thead>
                <tbody>
                  {top_users.map((u, idx) => (
                    <tr
                      key={u.id}
                      className="border-b last:border-b-0 border-gray-100"
                    >
                      <td className="py-2 pr-4 font-mono">#{idx + 1}</td>
                      <td className="py-2 pr-4">{u.username}</td>
                      <td className="py-2 text-right font-semibold">
                        {u.total_points}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Top Groups */}
        {SHOW_GROUPS && (
        <Card title="Top Groups (Premier League)">
          {top_groups.length === 0 ? (
            <p className="text-sm text-gray-600 py-4 text-center">
              No group data yet for this season.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-gray-500">
                    <th className="py-2 pr-4 text-left">Group</th>
                    <th className="py-2 pr-4 text-left">Members</th>
                    <th className="py-2 pr-4 text-right">Total Points</th>
                    <th className="py-2 text-right">Avg / Member</th>
                  </tr>
                </thead>
                <tbody>
                  {top_groups.map((g) => (
                    <tr
                      key={g.id}
                      className="border-b last:border-b-0 border-gray-100"
                    >
                      <td className="py-2 pr-4">{g.name}</td>
                      <td className="py-2 pr-4">{g.member_count}</td>
                      <td className="py-2 pr-4 text-right font-semibold">
                        {g.total_points}
                      </td>
                      <td className="py-2 text-right font-semibold">
                        {Number(g.avg_points).toFixed(1)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
        )}

        <Card>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-gray-700">
              Ready for the next season? Head back to the dashboard or update
              your predictions.
            </p>
            <div className="flex gap-2">
              <Link
                to="/dashboard"
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Go to Dashboard
              </Link>
              <Link
                to="/predictions"
                className="px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800 transition-colors"
              >
                Go to Predictions
              </Link>
            </div>
          </div>
        </Card>
      </main>
    </div>
  );
}


